// ============================================================
// usePings - the pings this client knows about, and how to send one.
//
// A ping is speech. Nothing here writes to a combatant, spends movement
// or touches the turn tracker, and no ping is ever part of undo history:
// undoing a sentence somebody said is not a thing the app should offer.
//
// EXPIRY IS LOCAL. Every client drops a ping on its own clock, a few
// seconds after it first held it (`PING_HELD_MS`, `useExpiry`, ours and
// everyone's), so a sender who closed their tab cannot leave a ring on
// anybody's screen. What is SEEN is shorter: the drawing fades in half a
// second from its first paint (BattlefieldOverlays). Each ping is held
// ONCE per screen: its document outlives the hold, and a later snapshot
// listing it must not bring it back. The document is tidied up by whoever
// sent it, purely so the collection does not grow for ever.
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { PING_ARRIVAL_STALE_MS, PING_HELD_MS, bornAt, canPing, makePing } from '../engine/battlefield/pings';

/**
 * The ping a sender makes. The note is worked out ONCE, by the sender:
 * recomputing per viewer would let two people read different sentences
 * off the same ring, and would go stale the moment anything moved.
 */
function pingFrom({ type, x, z, y, fromId, describeAt }, { playerId, playerName, describe }, now) {
  return makePing({
    type,
    x,
    z,
    ...(y ? { y } : {}),
    by: playerId,
    byName: playerName,
    at: now,
    ...(describe ? { note: describe({ x, y, z, type, describeAt }) || undefined } : {}),
    ...(fromId ? { fromId } : {}),
  });
}

const live = (now) => (p) => now - bornAt(p) < PING_HELD_MS;

/** Said recently enough, by its sender's clock, to be shown on arrival (not a joiner's reading of old pings). */
const fresh = (now) => (p) => now - p.at < PING_ARRIVAL_STALE_MS;

/**
 * The ids this screen has already held, so each ping is shown once: by id, to when it was first held, forgotten once
 * no snapshot could still list it (past `PING_ARRIVAL_STALE_MS`, it is refused as stale anyway).
 */
function useHeldOnce() {
  const held = useRef(new Map());
  return useCallback((pings, now) => {
    for (const [id, since] of held.current) if (now - since > PING_ARRIVAL_STALE_MS) held.current.delete(id);
    const unheld = pings.filter((p) => p && !held.current.has(p.id));
    for (const p of unheld) held.current.set(p.id, now);
    return unheld;
  }, []);
}

/** The same pings, in the same order: then the caller keeps its array, and nothing keyed on it restarts. */
const same = (a, b) => a.length === b.length && a.every((p, i) => p === b[i]);

/**
 * Remote pings folded into ours, by id, with every ping past its hold dropped. A ping already held keeps its object:
 * a ping never changes once said, and each snapshot parses new objects. A NEW one is stamped with this screen's clock
 * (`seenAt`): its hold runs from its arrival here, whatever the wire took or the sender's clock said.
 */
function mergedPings(prev, remote, now) {
  const byId = new Map(prev.filter(live(now)).map((p) => [p.id, p]));
  for (const ping of remote.filter((p) => p && fresh(now)(p))) {
    if (!byId.has(ping.id)) byId.set(ping.id, { ...ping, seenAt: now });
  }
  const next = [...byId.values()];
  return same(next, prev) ? prev : next;
}

/** Drop each ping once its hold is over, snapshot or not: a sender who closed the tab sends no snapshot to drop it. */
function useExpiry(pings, setPings) {
  useEffect(() => {
    if (!pings.length) return undefined;
    const due = Math.min(...pings.map(bornAt)) + PING_HELD_MS - Date.now();
    const id = setTimeout(() => setPings((prev) => mergedPings(prev, [], Date.now())), Math.max(0, due));
    return () => clearTimeout(id);
  }, [pings, setPings]);
}

/**
 * @param playerId   who is speaking - the player, not the combatant
 * @param playerName the name shown on the ring
 * @param describe   optional: given {x, z}, returns the note to stamp on it
 */
export default function usePings({ playerId = 'local', playerName = 'Someone', describe, onSend } = {}) {
  const [pings, setPings] = useState([]);
  const holdOnce = useHeldOnce();
  const sendPing = useSendPing({ playerId, playerName, describe, onSend }, setPings, holdOnce);
  useExpiry(pings, setPings);

  /**
   * Remote pings arriving from the session, merged with our own. Only ones
   * this screen never held (each snapshot lists every ping still in the
   * collection), and not stale ones: a client joining mid-session must not
   * be greeted by every ping still sitting there.
   */
  const mergePings = useCallback(
    (remote) => {
      const now = Date.now();
      const unheld = holdOnce(remote, now);
      if (unheld.length) setPings((prev) => mergedPings(prev, unheld, now));
    },
    [holdOnce],
  );

  return { pings, sendPing, mergePings };
}

/** Say something at a point: refused while rate-limited, shown here at once, then sent. */
function useSendPing({ playerId, playerName, describe, onSend }, setPings, holdOnce) {
  const lastAt = useRef(null);
  return useCallback(
    (args) => {
      const now = Date.now();
      // The ping-spam problem, solved where the ping is made rather than
      // where it is drawn: a refused ping should cost no document.
      if (!canPing(lastAt.current, now)) return null;
      lastAt.current = now;
      const ping = pingFrom(args, { playerId, playerName, describe }, now);
      // Held with its own `seenAt`, sent without it: that is this screen's, not the ping's.
      // Held once, so its echo from the server is not news.
      holdOnce([ping], now);
      setPings((prev) => [...prev, { ...ping, seenAt: now }]);
      // SHOWN LOCALLY FIRST, then sent. A ping is a gesture and it must
      // feel instant; waiting for the round trip would put the round trip
      // between the click and the ring.
      onSend?.(ping);
      return ping;
    },
    [playerId, playerName, describe, onSend, setPings, holdOnce],
  );
}
